<template>
  <LgModal
    :open="open"
    :title="alert ? 'Edit alert' : 'New alert'"
    :close-on-backdrop="false"
    @close="$emit('close')"
  >
    <div class="alert-form">
      <!-- ============ Name ============ -->
      <label class="field">
        <span class="field-label">Name</span>
        <LgInput
          :model-value="name"
          placeholder="e.g. API error rate too high"
          aria-label="Alert name"
          @update:model-value="name = $event"
        />
        <p v-if="errors.name" class="field-error" role="alert">{{ errors.name }}</p>
      </label>

      <!-- ============ Condition ============ -->
      <fieldset class="field group">
        <legend class="field-label">Condition</legend>

        <!-- Metric: picked chip, or the browsable catalog -->
        <div v-if="metricName && !pickerOpen" class="metric-picked">
          <LgChip tone="accent" :title="metricName">{{ metricName }}</LgChip>
          <LgButton size="sm" variant="ghost" @click="pickerOpen = true">Change metric</LgButton>
        </div>
        <div v-else class="metric-pick">
          <p class="hint">Pick the metric to watch — search and preview like on Metrics.</p>
          <div class="af-catalog">
            <MetricCatalog
              :metrics="catalog"
              :loading="catalogLoading"
              :error="catalogError"
              :search="catalogSearch"
              :selected-key="selectedCatalogKey"
              @update:search="catalogSearch = $event"
              @pick="onPickMetric"
            />
          </div>
        </div>
        <p v-if="errors.metric" class="field-error" role="alert">{{ errors.metric }}</p>

        <!-- Labels (optional, exact match) -->
        <div v-if="metricName" class="labels-row">
          <LgChip
            v-for="(v, k) in labels"
            :key="k"
            tone="neutral"
            removable
            :remove-label="`Remove label ${labelDisplayName(String(k))}`"
            @remove="removeLabel(String(k))"
          >
            {{ labelDisplayName(String(k)) }} = {{ v }}
          </LgChip>
          <AlertLabelPicker
            :metric-name="metricName"
            :source="metricSource"
            :target="metricTarget"
            :label-keys="labelKeyOptions"
            :existing-keys="Object.keys(labels)"
            @add="addLabel"
          />
          <span v-if="!Object.keys(labels).length" class="hint inline-hint">
            optional — narrow to one series (e.g. target=api)
          </span>
        </div>

        <!-- agg / window / operator / threshold -->
        <div class="cond-grid">
          <label class="opt">
            <span class="opt-label">Aggregation</span>
            <LgSelect
              :model-value="agg"
              :options="aggSelectOptions"
              aria-label="Aggregation"
              @update:model-value="agg = $event as MetricAgg"
            />
          </label>

          <label class="opt">
            <span class="opt-label">Evaluation window</span>
            <span class="dur-row">
              <LgInput
                :model-value="evalWindowValue"
                type="number"
                inputmode="numeric"
                min="0"
                class="dur-num"
                aria-label="Evaluation window amount"
                @update:model-value="evalWindowValue = $event"
              />
              <LgSelect
                :model-value="evalWindowUnit"
                :options="DURATION_UNITS"
                class="dur-unit"
                aria-label="Evaluation window unit"
                @update:model-value="evalWindowUnit = $event as DurationUnit"
              />
            </span>
            <span class="opt-hint">Aggregate the metric over the last …</span>
            <p v-if="errors.evalWindow" class="field-error" role="alert">
              {{ errors.evalWindow }}
            </p>
          </label>

          <label class="opt">
            <span class="opt-label">Is</span>
            <LgSelect
              :model-value="operator"
              :options="operatorOptions"
              aria-label="Operator"
              @update:model-value="operator = $event as AlertOperator"
            />
          </label>

          <label class="opt">
            <span class="opt-label">Threshold</span>
            <LgInput
              :model-value="thresholdText"
              type="number"
              inputmode="decimal"
              step="any"
              class="threshold-input"
              aria-label="Threshold"
              placeholder="e.g. 500"
              @update:model-value="thresholdText = $event"
            />
            <p v-if="errors.threshold" class="field-error" role="alert">{{ errors.threshold }}</p>
          </label>
        </div>
      </fieldset>

      <!-- ============ Timing ============ -->
      <fieldset class="field group">
        <legend class="field-label">Timing</legend>
        <div class="cond-grid">
          <label class="opt">
            <span class="opt-label">For duration</span>
            <span class="dur-row">
              <LgInput
                :model-value="forValue"
                type="number"
                inputmode="numeric"
                min="0"
                class="dur-num"
                aria-label="For duration amount"
                @update:model-value="forValue = $event"
              />
              <LgSelect
                :model-value="forUnit"
                :options="DURATION_UNITS"
                class="dur-unit"
                aria-label="For duration unit"
                @update:model-value="forUnit = $event as DurationUnit"
              />
            </span>
            <span class="opt-hint">
              Condition must hold continuously this long before firing. 0 = fire immediately.
            </span>
            <p v-if="errors.forMs" class="field-error" role="alert">{{ errors.forMs }}</p>
          </label>

          <label class="opt">
            <span class="opt-label">Cooldown</span>
            <span class="dur-row">
              <LgInput
                :model-value="cooldownValue"
                type="number"
                inputmode="numeric"
                min="0"
                class="dur-num"
                aria-label="Cooldown amount"
                @update:model-value="cooldownValue = $event"
              />
              <LgSelect
                :model-value="cooldownUnit"
                :options="DURATION_UNITS"
                class="dur-unit"
                aria-label="Cooldown unit"
                @update:model-value="cooldownUnit = $event as DurationUnit"
              />
            </span>
            <span class="opt-hint">
              Minimum gap between repeat notifications while still firing. 0 = notify once.
            </span>
            <p v-if="errors.cooldownMs" class="field-error" role="alert">{{ errors.cooldownMs }}</p>
          </label>
        </div>
      </fieldset>

      <!-- Live plain-language reading of the whole condition -->
      <p v-if="metricName && thresholdValid" class="sentence" data-testid="condition-sentence">
        {{ sentence }}
      </p>

      <!-- ============ Notification ============ -->
      <fieldset class="field group">
        <legend class="field-label">Discord notification</legend>

        <div class="mode-row" role="radiogroup" aria-label="Webhook">
          <button
            type="button"
            class="mode-btn"
            :class="{ active: webhookMode === 'default' }"
            role="radio"
            :aria-checked="webhookMode === 'default'"
            @click="webhookMode = 'default'"
          >
            Org default webhook
          </button>
          <button
            type="button"
            class="mode-btn"
            :class="{ active: webhookMode === 'custom' }"
            role="radio"
            :aria-checked="webhookMode === 'custom'"
            @click="webhookMode = 'custom'"
          >
            Custom webhook
          </button>
        </div>

        <p v-if="webhookMode === 'default' && !orgWebhookConfigured" class="warn-note">
          No org-default webhook is configured yet — this alert won't notify anyone until an admin
          sets one (from the Alerts page header) or you use a custom webhook.
        </p>

        <template v-if="webhookMode === 'custom'">
          <!-- Prefilled when editing: admin reads include the saved URL, so an
               unchanged save keeps it as-is. -->
          <LgInput
            :model-value="webhookUrl"
            type="text"
            autocomplete="off"
            placeholder="https://discord.com/api/webhooks/…"
            aria-label="Custom Discord webhook URL"
            @update:model-value="webhookUrl = $event"
          />
          <p class="hint">Discord webhooks only (https://discord.com/api/webhooks/…).</p>
          <p v-if="errors.webhookUrl" class="field-error" role="alert">{{ errors.webhookUrl }}</p>
        </template>

        <label class="field">
          <span class="opt-label">Message template <span class="optional">(optional)</span></span>
          <textarea
            v-model="messageTemplate"
            class="template-input"
            rows="2"
            :placeholder="DEFAULT_FIRE_TEMPLATE"
            aria-label="Message template"
          ></textarea>
          <span class="opt-hint">
            Variables:
            <code v-for="v in TEMPLATE_VARS" :key="v" class="var" v-text="varToken(v)"></code>
          </span>
          <p v-if="errors.messageTemplate" class="field-error" role="alert">
            {{ errors.messageTemplate }}
          </p>
          <span v-if="templatePreview" class="preview">
            <span class="preview-label">Preview:</span> {{ templatePreview }}
          </span>
        </label>

        <LgToggle v-model="notifyOnResolve">Notify when resolved</LgToggle>
      </fieldset>

      <LgToggle v-model="enabled">Enabled</LgToggle>

      <p v-if="saveError" class="field-error" role="alert">{{ saveError }}</p>
      <p v-if="createdNote" class="ok-note" role="status">
        Alert created — send a test to make sure it reaches Discord.
      </p>

      <!-- ============ Send test (the "does my webhook work" affordance) ============ -->
      <div v-if="currentId !== null" class="test-box">
        <LgButton class="af-test-btn" variant="primary" :disabled="testing" @click="onSendTest">
          <template #icon><Send :size="15" aria-hidden="true" /></template>
          {{ testing ? 'Sending test…' : 'Send test to Discord' }}
        </LgButton>
        <span class="test-hint">Sends a sample message using the alert's saved settings.</span>
        <p
          v-if="testResult"
          class="test-result"
          :class="testResult.ok ? 'ok' : 'bad'"
          role="status"
        >
          {{ testResult.text }}
        </p>
      </div>
    </div>

    <template #footer>
      <LgButton variant="ghost" @click="$emit('close')">
        {{ createdNote ? 'Done' : 'Cancel' }}
      </LgButton>
      <LgButton class="af-save-btn" variant="primary" :disabled="saving" @click="onSave">
        {{ saving ? 'Saving…' : currentId !== null ? 'Save changes' : 'Create alert' }}
      </LgButton>
    </template>
  </LgModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { Send } from 'lucide-vue-next';
import {
  api,
  ApiError,
  type Alert,
  type AlertInput,
  type AlertOperator,
  type CatalogMetric,
  type MetricAgg,
} from '../../api.js';
import {
  ALERT_OPERATORS,
  ALL_ALERT_AGGS,
  DEFAULT_EVAL_WINDOW_MS,
  DEFAULT_FIRE_TEMPLATE,
  DURATION_UNITS,
  MAX_ALERT_NAME_LEN,
  MAX_ALERT_TEMPLATE_LEN,
  MAX_ALERT_WEBHOOK_URL_LEN,
  MAX_COOLDOWN_MS,
  MAX_EVAL_WINDOW_MS,
  MAX_FOR_MS,
  OPERATOR_WORD,
  TEMPLATE_VARS,
  conditionSentence,
  formatDuration,
  isDiscordWebhookUrl,
  msToParts,
  partsToMs,
  renderTemplate,
  sampleTemplateVars,
  type DurationUnit,
} from '../../lib/alerting.js';
import {
  HISTOGRAM_BUCKET_LABEL,
  RESERVED_TARGET_LABEL,
  aggOptions,
  defaultAgg,
  labelDisplayName,
} from '../../lib/metric-exploration.js';
import { RANGE_MS } from '../../composables/useFilters.js';
import MetricCatalog from '../metrics/MetricCatalog.vue';
import AlertLabelPicker from './AlertLabelPicker.vue';
import LgModal from '../../ui/LgModal.vue';
import LgInput from '../../ui/LgInput.vue';
import LgSelect from '../../ui/LgSelect.vue';
import LgButton from '../../ui/LgButton.vue';
import LgToggle from '../../ui/LgToggle.vue';
import LgChip from '../../ui/LgChip.vue';

const props = defineProps<{
  open: boolean;
  /** null = create a new alert; otherwise edit this one. */
  alert: Alert | null;
  /** Whether the org-default webhook is configured (for the fallback warning). */
  orgWebhookConfigured: boolean;
}>();

const emit = defineEmits<{ close: []; saved: [] }>();

// --- Catalog (metric picker) ------------------------------------------------
const catalog = ref<CatalogMetric[]>([]);
const catalogLoading = ref(false);
const catalogError = ref(false);
const catalogSearch = ref('');
const pickerOpen = ref(false);
let catalogLoadedOnce = false;

async function loadCatalog() {
  catalogLoading.value = true;
  try {
    const now = Date.now();
    const res = await api.metrics.catalog({ from: now - RANGE_MS['1h'], to: now });
    catalog.value = res.metrics;
    catalogError.value = false;
    syncMetricMeta();
  } catch {
    catalogError.value = true;
  } finally {
    catalogLoading.value = false;
  }
}

// --- Form state ---------------------------------------------------------------
const name = ref('');
const metricName = ref('');
const metricSource = ref('');
const metricTarget = ref('');
const metricType = ref<string | null>(null);
const metricLabelKeys = ref<string[]>([]);
const labels = ref<Record<string, string>>({});
const agg = ref<MetricAgg>('avg');
const operator = ref<AlertOperator>('>');
const thresholdText = ref('');
const evalWindowValue = ref('1');
const evalWindowUnit = ref<DurationUnit>('m');
const forValue = ref('0');
const forUnit = ref<DurationUnit>('m');
const cooldownValue = ref('0');
const cooldownUnit = ref<DurationUnit>('m');
const webhookMode = ref<'default' | 'custom'>('default');
const webhookUrl = ref('');
const messageTemplate = ref('');
const notifyOnResolve = ref(true);
const enabled = ref(true);

const errors = ref<Record<string, string>>({});
const saveError = ref('');
const saving = ref(false);
/** Set after a successful create so the modal stays open with the test button. */
const savedId = ref<number | null>(null);
const createdNote = ref(false);

const currentId = computed<number | null>(() => props.alert?.id ?? savedId.value);

/** Re-seed the whole form whenever the modal opens. */
watch(
  () => props.open,
  (open) => {
    if (!open) return;
    errors.value = {};
    saveError.value = '';
    savedId.value = null;
    createdNote.value = false;
    testResult.value = null;
    catalogSearch.value = '';
    const a = props.alert;
    name.value = a?.name ?? '';
    metricName.value = a?.metric ?? '';
    metricSource.value = '';
    metricTarget.value = '';
    metricType.value = null;
    metricLabelKeys.value = [];
    labels.value = a ? { ...a.labels } : {};
    agg.value = a?.agg ?? 'avg';
    operator.value = a?.operator ?? '>';
    thresholdText.value = a ? String(a.threshold) : '';
    setDuration(evalWindowValue, evalWindowUnit, a?.evalWindowMs ?? DEFAULT_EVAL_WINDOW_MS);
    setDuration(forValue, forUnit, a?.forMs ?? 0);
    setDuration(cooldownValue, cooldownUnit, a?.cooldownMs ?? 0);
    // Admin reads include the saved override URL, so editing prefills it and an
    // unchanged save simply re-sends the same URL (no re-entry needed). The
    // org-default/custom choice reflects what's actually stored.
    webhookMode.value = a?.hasWebhookOverride ? 'custom' : 'default';
    webhookUrl.value = a?.webhookUrl ?? '';
    messageTemplate.value = a?.messageTemplate ?? '';
    notifyOnResolve.value = a?.notifyOnResolve ?? true;
    enabled.value = a?.enabled ?? true;
    pickerOpen.value = false;
    if (!catalogLoadedOnce) {
      catalogLoadedOnce = true;
      loadCatalog();
    } else {
      syncMetricMeta();
    }
  },
);

function setDuration(
  valueRef: { value: string },
  unitRef: { value: DurationUnit },
  ms: number,
): void {
  const parts = msToParts(ms);
  valueRef.value = String(parts.value);
  unitRef.value = parts.unit;
}

/** When editing, recover the metric's type/labelKeys from the catalog by name
 * (best-effort — the metric may have aged out of the catalog window). */
function syncMetricMeta() {
  if (!metricName.value || metricType.value !== null) {
    if (metricName.value && !metricLabelKeys.value.length) {
      const m = catalog.value.find((c) => c.name === metricName.value);
      if (m) metricLabelKeys.value = m.labelKeys;
    }
    return;
  }
  const m = catalog.value.find((c) => c.name === metricName.value);
  if (m) {
    metricType.value = m.type;
    metricSource.value = m.source;
    metricTarget.value = m.target;
    metricLabelKeys.value = m.labelKeys;
  }
}

function onPickMetric(m: CatalogMetric) {
  const changed = m.name !== metricName.value;
  metricName.value = m.name;
  metricSource.value = m.source;
  metricTarget.value = m.target;
  metricType.value = m.type;
  metricLabelKeys.value = m.labelKeys;
  if (changed) {
    labels.value = {};
    agg.value = defaultAgg(m.type);
  }
  pickerOpen.value = false;
}

const selectedCatalogKey = computed(() =>
  metricSource.value && metricName.value
    ? `${metricSource.value} ${metricTarget.value} ${metricName.value}`
    : null,
);

// --- Labels ---------------------------------------------------------------------
const labelKeyOptions = computed(() => [
  RESERVED_TARGET_LABEL,
  ...metricLabelKeys.value.filter((k) => k !== HISTOGRAM_BUCKET_LABEL),
]);

function addLabel(key: string, value: string) {
  labels.value = { ...labels.value, [key]: value };
}
function removeLabel(key: string) {
  const next = { ...labels.value };
  delete next[key];
  labels.value = next;
}

// --- Options ----------------------------------------------------------------------
const aggSelectOptions = computed(() => {
  const opts = metricType.value !== null ? aggOptions(metricType.value) : [...ALL_ALERT_AGGS];
  // Keep the saved agg selectable even if the inferred type wouldn't offer it.
  if (!opts.includes(agg.value)) opts.push(agg.value);
  return opts.map((a) => ({ value: a, label: a }));
});
const operatorOptions = ALERT_OPERATORS.map((op) => ({
  value: op,
  label: `${op}  (${OPERATOR_WORD[op]})`,
}));

// --- Derived -------------------------------------------------------------------------
const threshold = computed(() => Number(thresholdText.value));
const thresholdValid = computed(
  () => thresholdText.value.trim() !== '' && Number.isFinite(threshold.value),
);
const evalWindowMs = computed(() => partsToMs(Number(evalWindowValue.value), evalWindowUnit.value));
const forMs = computed(() => partsToMs(Number(forValue.value), forUnit.value));
const cooldownMs = computed(() => partsToMs(Number(cooldownValue.value), cooldownUnit.value));

const sentence = computed(() =>
  conditionSentence({
    metric: metricName.value,
    labels: labels.value,
    agg: agg.value,
    operator: operator.value,
    threshold: threshold.value,
    evalWindowMs: evalWindowMs.value,
    forMs: forMs.value,
  }),
);

/** `alert` → `{{alert}}` — built in JS because literal `{{` inside a template
 * interpolation would fight Vue's own delimiters. */
function varToken(name: string): string {
  return `{{${name}}}`;
}

const templatePreview = computed(() => {
  const tpl = messageTemplate.value.trim();
  if (!tpl) return '';
  return renderTemplate(
    tpl,
    sampleTemplateVars({
      name: name.value,
      metric: metricName.value,
      labels: labels.value,
      operator: operator.value,
      threshold: thresholdValid.value ? threshold.value : 0,
    }),
  );
});

// --- Validation + save ------------------------------------------------------------------
function validate(): boolean {
  const errs: Record<string, string> = {};
  if (!name.value.trim()) errs.name = 'Give the alert a name.';
  else if (name.value.trim().length > MAX_ALERT_NAME_LEN)
    errs.name = `Keep the name under ${String(MAX_ALERT_NAME_LEN)} characters.`;
  if (!metricName.value) errs.metric = 'Pick a metric to watch.';
  if (!thresholdValid.value) errs.threshold = 'Enter a numeric threshold.';
  if (evalWindowMs.value <= 0) errs.evalWindow = 'The evaluation window must be more than 0.';
  else if (evalWindowMs.value > MAX_EVAL_WINDOW_MS)
    errs.evalWindow = `Keep the window at or under ${formatDuration(MAX_EVAL_WINDOW_MS)}.`;
  if (forMs.value > MAX_FOR_MS)
    errs.forMs = `Keep the for-duration at or under ${formatDuration(MAX_FOR_MS)}.`;
  if (cooldownMs.value > MAX_COOLDOWN_MS)
    errs.cooldownMs = `Keep the cooldown at or under ${formatDuration(MAX_COOLDOWN_MS)}.`;
  if (webhookMode.value === 'custom') {
    const url = webhookUrl.value.trim();
    if (!url) {
      errs.webhookUrl = 'Paste a Discord webhook URL, or switch to the org default.';
    } else if (url.length > MAX_ALERT_WEBHOOK_URL_LEN || !isDiscordWebhookUrl(url)) {
      errs.webhookUrl = 'Must be a Discord webhook URL (https://discord.com/api/webhooks/…).';
    }
  }
  if (messageTemplate.value.trim().length > MAX_ALERT_TEMPLATE_LEN)
    errs.messageTemplate = `Keep the template under ${String(MAX_ALERT_TEMPLATE_LEN)} characters.`;
  errors.value = errs;
  return Object.keys(errs).length === 0;
}

function buildInput(): AlertInput {
  return {
    name: name.value.trim(),
    metric: metricName.value,
    labels: labels.value,
    agg: agg.value,
    operator: operator.value,
    threshold: threshold.value,
    evalWindowMs: evalWindowMs.value,
    forMs: forMs.value,
    cooldownMs: cooldownMs.value,
    webhookUrl: webhookMode.value === 'custom' ? webhookUrl.value.trim() : null,
    messageTemplate: messageTemplate.value.trim() || null,
    notifyOnResolve: notifyOnResolve.value,
    enabled: enabled.value,
  };
}

async function onSave() {
  saveError.value = '';
  if (!validate() || saving.value) return;
  saving.value = true;
  try {
    if (currentId.value !== null) {
      await api.alerts.update(currentId.value, buildInput());
      emit('saved');
      emit('close');
    } else {
      const { id } = await api.alerts.create(buildInput());
      savedId.value = id;
      createdNote.value = true;
      emit('saved');
    }
  } catch (e) {
    saveError.value = e instanceof ApiError ? e.message : 'Couldn’t save the alert.';
  } finally {
    saving.value = false;
  }
}

// --- Test webhook ------------------------------------------------------------------------
const testing = ref(false);
const testResult = ref<{ ok: boolean; text: string } | null>(null);

async function onSendTest() {
  const id = currentId.value;
  if (id === null || testing.value) return;
  testing.value = true;
  testResult.value = null;
  try {
    const res = await api.alerts.test(id);
    testResult.value = res.ok
      ? {
          ok: true,
          text: `Delivered — Discord answered HTTP ${String(res.status)}. Check the channel!`,
        }
      : {
          ok: false,
          text:
            res.status !== null
              ? `Discord rejected it (HTTP ${String(res.status)}) — check the webhook URL.`
              : 'The webhook request never completed (network error or timeout).',
        };
  } catch (e) {
    testResult.value = {
      ok: false,
      text: e instanceof ApiError ? e.message : 'Test failed — try again.',
    };
  } finally {
    testing.value = false;
  }
}
</script>

<style scoped>
.alert-form {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-4);
}
.field {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-2);
  border: none;
  margin: 0;
  padding: 0;
  min-width: 0;
}
.field.group {
  gap: var(--lg-space-3);
}
.field-label {
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0;
}
.optional {
  text-transform: none;
  letter-spacing: normal;
}
.field-error {
  margin: 0;
  font-size: var(--lg-fs-sm);
  color: var(--lg-danger);
}
.hint,
.opt-hint {
  margin: 0;
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
}
.inline-hint {
  align-self: center;
}
.warn-note {
  margin: 0;
  font-size: var(--lg-fs-sm);
  color: var(--lg-warn);
}
.ok-note {
  margin: 0;
  font-size: var(--lg-fs-sm);
  color: var(--lg-ok);
}

.metric-picked {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  flex-wrap: wrap;
  min-width: 0;
}
.metric-pick {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-2);
}
.af-catalog {
  height: 280px;
  border: 1px solid var(--lg-border);
  border-radius: var(--lg-radius-md);
  overflow: hidden;
}

.labels-row {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  flex-wrap: wrap;
}

.cond-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--lg-space-3);
}
@media (max-width: 480px) {
  .cond-grid {
    grid-template-columns: 1fr;
  }
}
.opt {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-1);
  min-width: 0;
}
.opt-label {
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.dur-row {
  display: flex;
  gap: var(--lg-space-2);
  min-width: 0;
}
.dur-num {
  flex: 1;
  min-width: 0;
}
.dur-unit {
  flex-shrink: 0;
  width: 112px;
}

.sentence {
  margin: 0;
  padding: var(--lg-space-2) var(--lg-space-3);
  font-size: var(--lg-fs-sm);
  color: var(--lg-text-dim);
  border-left: 3px solid var(--lg-accent);
  background: var(--lg-bg-surface);
  border-radius: var(--lg-radius-sm);
}

.mode-row {
  display: inline-flex;
  border: 1px solid var(--lg-border-strong);
  border-radius: var(--lg-radius-md);
  overflow: hidden;
  align-self: flex-start;
}
.mode-btn {
  font: inherit;
  font-size: var(--lg-fs-sm);
  min-height: var(--lg-control-md);
  padding: 0 var(--lg-space-3);
  background: var(--lg-bg-surface);
  color: var(--lg-text-dim);
  border: none;
  border-right: 1px solid var(--lg-border);
  cursor: pointer;
}
.mode-btn:last-child {
  border-right: none;
}
.mode-btn:hover {
  color: var(--lg-text);
}
.mode-btn.active {
  background: var(--lg-accent);
  color: var(--lg-accent-fg);
  font-weight: 600;
}
.mode-btn:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px var(--lg-accent);
}

.template-input {
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
  padding: var(--lg-space-2);
  border-radius: var(--lg-radius-md);
  border: 1px solid var(--lg-border-strong);
  background: var(--lg-bg);
  color: var(--lg-text);
  resize: vertical;
  min-width: 0;
  max-width: 100%;
}
.template-input:focus {
  outline: none;
  border-color: var(--lg-accent);
  box-shadow: var(--lg-focus);
}
.template-input::placeholder {
  color: var(--lg-text-faint);
}
.var {
  font-family: var(--lg-font-mono);
  background: var(--lg-bg-surface);
  border: 1px solid var(--lg-border-faint);
  border-radius: var(--lg-radius-sm);
  padding: 0 3px;
  margin-right: 3px;
}
.preview {
  font-size: var(--lg-fs-sm);
  color: var(--lg-text-dim);
  overflow-wrap: anywhere;
}
.preview-label {
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-xs);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-right: var(--lg-space-1);
}

.test-box {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-2);
  padding: var(--lg-space-3);
  border: 1px dashed var(--lg-border-strong);
  border-radius: var(--lg-radius-md);
  background: var(--lg-bg-surface);
}
.af-test-btn {
  align-self: flex-start;
}
.test-hint {
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
}
.test-result {
  margin: 0;
  font-size: var(--lg-fs-sm);
}
.test-result.ok {
  color: var(--lg-ok);
}
.test-result.bad {
  color: var(--lg-danger);
}
</style>
